import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, FolderInput, FileArchive, X, Loader2, CheckSquare, Square } from "lucide-react";

function Conferma({ testo, bottone, onPick }) {
  return (
    <div className="fixed inset-0 z-[220] bg-black/40 flex items-center justify-center p-4" data-testid="conferma-blocco" onClick={() => onPick(false)}>
      <div className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <p className="text-sm font-semibold mb-4" data-testid="conferma-blocco-testo">{testo}</p>
        <div className="flex justify-end gap-2">
          <button data-testid="conferma-blocco-annulla" onClick={() => onPick(false)} className="px-4 py-2 rounded-lg border border-[var(--border)] text-sm font-semibold">Annulla</button>
          <button data-testid="conferma-blocco-ok" onClick={() => onPick(true)} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold">{bottone}</button>
        </div>
      </div>
    </div>
  );
}

function ConfermaScritta({ n, onPick }) {
  const [v, setV] = useState("");
  const ok = v.trim() === "ELIMINA";
  return (
    <div className="fixed inset-0 z-[220] bg-black/40 flex items-center justify-center p-4" data-testid="conferma-elimina-def" onClick={() => onPick(false)}>
      <div className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <p className="text-sm font-semibold mb-1">Eliminare definitivamente {n} {n === 1 ? "elemento" : "elementi"}?</p>
        <p className="text-xs text-slate-600 mb-3">Non si potranno più recuperare. Scrivi <b>ELIMINA</b> per confermare.</p>
        <input autoFocus data-testid="conferma-elimina-def-input" value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && ok) onPick(true); }}
          placeholder="ELIMINA" className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-base sm:text-sm mb-4" />
        <div className="flex justify-end gap-2">
          <button data-testid="conferma-elimina-def-annulla" onClick={() => onPick(false)} className="px-4 py-2 rounded-lg border border-[var(--border)] text-sm font-semibold">Annulla</button>
          <button data-testid="conferma-elimina-def-ok" disabled={!ok} onClick={() => onPick(true)} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-40">Elimina definitivamente</button>
        </div>
      </div>
    </div>
  );
}

export const chiediElimina = (n) => new Promise((resolve) => {
  const el = document.createElement("div");
  document.body.appendChild(el);
  const root = createRoot(el);
  root.render(<ConfermaScritta n={n} onPick={(x) => { root.unmount(); el.remove(); resolve(x); }} />);
});

export const chiediConferma = (testo, bottone = "Sposta nel cestino") => new Promise((resolve) => {
  const el = document.createElement("div");
  document.body.appendChild(el);
  const root = createRoot(el);
  root.render(<Conferma testo={testo} bottone={bottone} onPick={(x) => { root.unmount(); el.remove(); resolve(x); }} />);
});

// Esito di un'azione in blocco: i 403 contano come "saltati (senza permesso)", gli altri errori come "non riusciti".
export const esito = (r, verbo = "fatti") => {
  const ok = r.filter((x) => x.status === "fulfilled").length;
  const perm = r.filter((x) => x.status === "rejected" && x.reason?.response?.status === 403).length;
  const altri = r.length - ok - perm;
  const motivi = [...new Set(r.filter((x) => x.status === "rejected").map((x) => apiError(x.reason) || "errore sconosciuto"))];
  const msg = `${ok} ${verbo}${perm ? `, ${perm} saltati (senza permesso)` : ""}${altri ? `, ${altri} non riusciti` : ""}${motivi.length ? `. Motivo: ${motivi.join("; ")}` : ""}`;
  (ok ? toast.success : toast.error)(msg);
};

// Una conferma, poi 5 secondi per annullare; solo dopo i file vanno nel cestino.
export async function eliminaInBlocco(ids, onDone) {
  if (!ids.length || !(await chiediConferma(`Spostare ${ids.length} file nel cestino?`))) return false;
  let annullato = false;
  toast(`${ids.length} file nel cestino tra 5 secondi`, { id: "elimina-blocco", duration: 5000, action: { label: "Annulla", onClick: () => { annullato = true; toast.message("Eliminazione annullata"); } } });
  setTimeout(async () => {
    if (annullato) return;
    const r = await Promise.allSettled(ids.map((id) => api.delete(`/files/${id}`)));
    esito(r, "spostati nel cestino");
    onDone && onDone();
  }, 5000);
  return true;
}

export async function spostaInBlocco(immobileId, ids, dest, onDone, etichetta) {
  const r = await Promise.allSettled(ids.map((id) => api.post(`/documenti/pratica/${immobileId}/sposta`, { file_id: id, sottocartella: dest })));
  esito(r, `spostati in ${etichetta || dest}`);
  onDone && onDone();
}

export async function zipInBlocco(ids) {
  try {
    const r = await api.post("/files/zip", { ids }, { responseType: "blob", timeout: 600000 });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = (/filename="?([^";]+)"?/.exec(r.headers?.["content-disposition"] || "") || [])[1] || "File_selezionati.zip";
    a.click();
    toast.success("Zip scaricato");
  } catch (e) { toast.error(apiError(e)); }
}

async function scaricaBlob(r, nome) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = (/filename="?([^";]+)"?/.exec(r.headers?.["content-disposition"] || "") || [])[1] || nome;
  a.click();
}

export async function zipAllegati(tipo, id, aids) {
  try { await scaricaBlob(await api.post("/allegati/zip", { tipo, id, aids }, { responseType: "blob", timeout: 600000 }), "Allegati_selezionati.zip"); toast.success("Zip scaricato"); }
  catch (e) { toast.error(e?.response?.status === 403 ? "Nessun allegato scaricabile tra quelli selezionati." : apiError(e)); }
}

export async function eliminaAllegati(tipo, id, aids) {
  if (!(await chiediConferma(`Spostare ${aids.length} ${aids.length === 1 ? "allegato" : "allegati"} nel cestino? Si recuperano da Documenti → Cestino.`))) return false;
  try {
    const { data } = await api.post("/allegati/elimina", { tipo, id, aids });
    (data.eliminati ? toast.success : toast.error)(`${data.eliminati} spostati nel cestino${data.saltati ? `, ${data.saltati} saltati (senza permesso)` : ""}`);
    return true;
  } catch (e) { toast.error(apiError(e)); return false; }
}

// Barra in linea (anche dentro le finestre) per gli allegati di task, messaggi, riunioni e fatture.
export function BarraAllegati({ S, ids, tipo, id, onDone, testid = "allegati" }) {
  const [busy, setBusy] = useState(null);
  if (!ids.length) return null;
  const scelti = [...S.sel].filter((x) => ids.includes(x));
  const fai = async (k, fn) => { setBusy(k); try { await fn(); } finally { setBusy(null); } };
  const btn = "inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold disabled:opacity-40";
  return (
    <div data-testid={`${testid}-barra`} className={`flex items-center gap-2 flex-wrap rounded-xl ${scelti.length ? "bg-[#111111] text-white px-3 py-1.5" : ""}`}>
      {scelti.length ? <span data-testid={`${testid}-n`} className="text-xs font-bold">{scelti.length} selezionati</span>
        : <SelezionaTutto ids={ids} sel={S.sel} onTutti={S.tutti} testid={`${testid}-seltutto`} />}
      {scelti.length > 0 && <>
        <button type="button" data-testid={`${testid}-seltutto-on`} onClick={() => S.tutti(ids)} className={`${btn} hover:bg-white/10`}>{ids.every((i) => S.sel.has(i)) ? "Deseleziona tutto" : "Seleziona tutto"}</button>
        <button type="button" data-testid={`${testid}-elimina`} disabled={!!busy} onClick={() => fai("del", async () => { if (await eliminaAllegati(tipo, id, scelti)) { S.annulla(); onDone && onDone(); } })} className={`${btn} bg-red-600`}>{busy === "del" ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Elimina</button>
        <button type="button" data-testid={`${testid}-zip`} disabled={!!busy} onClick={() => fai("zip", () => zipAllegati(tipo, id, scelti))} className={`${btn} bg-white/15`}>{busy === "zip" ? <Loader2 size={12} className="animate-spin" /> : <FileArchive size={12} />} Scarica (zip)</button>
        <button type="button" data-testid={`${testid}-annulla`} onClick={S.annulla} className={`${btn} ml-auto hover:bg-white/10`}><X size={12} /> Annulla selezione</button>
      </>}
    </div>
  );
}

// Azioni standard dei cestini: Ripristina e (solo per chi può) Elimina definitivamente con ELIMINA scritto.
export const azioniCestino = (ripristina, elimina) => [
  { k: "ripristina", label: "Ripristina", esito: "ripristinati", run: ripristina },
  ...(elimina ? [{ k: "elimina-def", label: "Elimina definitivamente", danger: true, scrivi: true, esito: "eliminati definitivamente", run: elimina }] : []),
];

// Mentre la barra fissa è aperta, il contenuto della pagina scende della sua altezza (non copre titoli e linguette).
let nBarre = 0;
function useSpazioBarra(attiva) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!attiva || !el) return undefined;
    nBarre += 1;
    document.body.classList.add("sel-attiva");
    const set = () => document.documentElement.style.setProperty("--barra-sel-h", `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => { ro.disconnect(); nBarre -= 1; if (!nBarre) document.body.classList.remove("sel-attiva"); };
  }, [attiva]);
  return ref;
}

export function useSelezione() {
  const [sel, setSel] = useState(() => new Set());
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const tutti = (ids) => setSel((s) => { const n = new Set(s); const on = ids.every((i) => n.has(i)); ids.forEach((i) => (on ? n.delete(i) : n.add(i))); return n; });
  return { sel, setSel, toggle, tutti, annulla: () => setSel(new Set()) };
}

// Tenendo premuto (telefono) si entra in modalità selezione.
export function usePressioneLunga(cb, ms = 500) {
  const t = useRef(null);
  const fired = useRef(false);
  const stop = () => clearTimeout(t.current);
  return {
    onTouchStart: () => { fired.current = false; t.current = setTimeout(() => { fired.current = true; if (navigator.vibrate) navigator.vibrate(20); cb(); }, ms); },
    onTouchEnd: (e) => { stop(); if (fired.current) e.preventDefault(); },
    onTouchMove: stop,
    onClickCapture: (e) => { if (fired.current) { fired.current = false; e.stopPropagation(); e.preventDefault(); } },
    onContextMenu: (e) => { if (t.current) e.preventDefault(); },
  };
}

export function SelezionaTutto({ ids, sel, onTutti, testid }) {
  const on = ids.length > 0 && ids.every((i) => sel.has(i));
  return (
    <button type="button" data-testid={testid} onClick={(e) => { e.stopPropagation(); onTutti(ids); }} disabled={!ids.length}
      className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-[#111111]">
      {on ? <CheckSquare size={15} className="text-[#FC82ED]" /> : <Square size={15} />} Seleziona tutto
    </button>
  );
}

export function BarraSelezione({ sel, onAnnulla, onElimina, destinazioni = [], onSposta, onZip }) {
  const [busy, setBusy] = useState(null);
  const [dest, setDest] = useState("");
  const ref = useSpazioBarra(sel.size > 0);
  if (!sel.size) return null;
  const fai = async (k, fn) => { setBusy(k); try { await fn(); } finally { setBusy(null); } };
  const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold";
  return createPortal(
    <div ref={ref} data-testid="barra-selezione" className="fixed top-[72px] inset-x-0 mx-auto z-50 w-[min(960px,calc(100%-1.5rem))] flex items-center gap-2 flex-wrap px-3 py-2 rounded-xl bg-[#111111] text-white shadow-lg">
      <span className="text-sm font-bold mr-1" data-testid="barra-selezione-n">{sel.size} selezionati</span>
      {onElimina && <button data-testid="barra-elimina" disabled={!!busy} onClick={() => fai("del", onElimina)} className={`${btn} bg-red-600`}>{busy === "del" ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Elimina</button>}
      {onSposta && destinazioni.length > 0 && (
        <span className="inline-flex items-center gap-1">
          <select data-testid="barra-sposta-dest" value={dest} onChange={(e) => setDest(e.target.value)} className="text-xs text-[#111111] rounded-lg px-2 py-1.5">
            <option value="">Sposta in…</option>{destinazioni.map((d) => <option key={d} value={d}>{d}</option>)}</select>
          <button data-testid="barra-sposta" disabled={!dest || !!busy} onClick={() => fai("mv", () => onSposta(dest))} className={`${btn} bg-white/15 disabled:opacity-40`}>{busy === "mv" ? <Loader2 size={13} className="animate-spin" /> : <FolderInput size={13} />} Sposta</button>
        </span>)}
      {onZip && <button data-testid="barra-zip" disabled={!!busy} onClick={() => fai("zip", onZip)} className={`${btn} bg-white/15`}>{busy === "zip" ? <Loader2 size={13} className="animate-spin" /> : <FileArchive size={13} />} Scarica (zip)</button>}
      <button data-testid="barra-annulla" onClick={onAnnulla} className={`${btn} ml-auto hover:bg-white/10`}><X size={13} /> Annulla selezione</button>
    </div>,
    document.body
  );
}

// Casella di selezione sovrapposta a una card/riga di elenco (la card deve essere "relative").
export function Casella({ on, onToggle, testid, className = "top-2 left-2", inline }) {
  return (
    <button type="button" data-testid={testid} onClick={(e) => { e.stopPropagation(); onToggle(); }} title="Seleziona"
      className={`${inline ? "relative shrink-0 w-5 h-5" : `absolute z-10 w-6 h-6 ${className}`} rounded-md border-2 flex items-center justify-center ${on ? "bg-[#FC82ED] border-[#FC82ED] text-black" : "bg-white/95 border-slate-300 hover:border-[#FC82ED]"}`}>
      {on && <CheckSquare size={13} />}
    </button>
  );
}

export function esportaCsv(nome, righe, colonne) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [colonne.map(([, l]) => esc(l)).join(";"), ...righe.map((r) => colonne.map(([k]) => esc(typeof k === "function" ? k(r) : r[k])).join(";"))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
  a.download = `${nome}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  toast.success(`${righe.length} righe esportate`);
}

// Barra azioni in blocco per gli elenchi: ogni azione viene tentata su ogni elemento; i 403 contano come "saltati".
export function BarraElenco({ sel, onAnnulla, azioni = [], cambiaAgente, onEsporta, onFatto }) {
  const [busy, setBusy] = useState(null);
  const [agenti, setAgenti] = useState(null);
  const [ag, setAg] = useState("");
  const ref = useSpazioBarra(sel.size > 0);
  if (!sel.size) return null;
  const ids = [...sel];
  const fai = async (a, extra) => {
    if (a.conferma && !(await chiediConferma(a.conferma(ids.length), a.bottone || a.label))) return;
    if (a.scrivi && !(await chiediElimina(ids.length))) return;
    setBusy(a.k);
    const r = await Promise.allSettled(ids.map((id) => a.run(id, extra)));
    setBusy(null);
    esito(r, a.esito || "fatti");
    onAnnulla(); onFatto && onFatto();
  };
  const caricaAgenti = () => { if (!agenti) api.get("/agents?con_soci=true").then((r) => setAgenti(r.data || [])).catch(() => setAgenti([])); };
  const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-40";
  return createPortal(
    <div ref={ref} data-testid="barra-elenco" className="fixed top-[72px] inset-x-0 mx-auto z-50 w-[min(960px,calc(100%-1.5rem))] flex items-center gap-2 flex-wrap px-3 py-2 rounded-xl bg-[#111111] text-white shadow-lg">
      <span className="text-sm font-bold mr-1" data-testid="barra-elenco-n">{sel.size} selezionati</span>
      {azioni.map((a) => (
        <button key={a.k} data-testid={`barra-elenco-${a.k}`} disabled={!!busy} onClick={() => fai(a)} className={`${btn} ${a.danger ? "bg-red-600" : "bg-white/15"}`}>
          {busy === a.k && <Loader2 size={13} className="animate-spin" />}{a.label}</button>))}
      {cambiaAgente && (
        <span className="inline-flex items-center gap-1">
          <select data-testid="barra-elenco-agente" value={ag} onFocus={caricaAgenti} onMouseDown={caricaAgenti} onChange={(e) => setAg(e.target.value)} className="text-xs text-[#111111] rounded-lg px-2 py-1.5 max-w-[11rem]">
            <option value="">Cambia agente…</option>{(agenti || []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
          <button data-testid="barra-elenco-agente-ok" disabled={!ag || !!busy} onClick={() => fai({ k: "agente", run: cambiaAgente, esito: "assegnati al nuovo agente" }, ag)} className={`${btn} bg-white/15`}>{busy === "agente" && <Loader2 size={13} className="animate-spin" />}Assegna</button>
        </span>)}
      {onEsporta && <button data-testid="barra-elenco-esporta" onClick={() => onEsporta(ids)} className={`${btn} bg-white/15`}>Esporta</button>}
      <button data-testid="barra-elenco-annulla" onClick={onAnnulla} className={`${btn} ml-auto hover:bg-white/10`}><X size={13} /> Annulla selezione</button>
    </div>,
    document.body
  );
}

